import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import { Card, FontWeights, Spinner, Stack, Text } from "@bps/fluent-ui";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { useIntegrationScreenContext } from "../context/IntegrationScreenContext.ts";
import { XeroButton } from "./Xero/XeroButton.tsx";
import { XeroViewSelector } from "./Xero/XeroViewSelector.tsx";

export const XeroConfigurationCard: React.FC = observer(() => {
  const context = useIntegrationScreenContext();
  const { customIntegrationXero } = useStores();
  const { showXeroConfigurationCard } = context;

  // Storing this promise outside the DataFetcher so that it isn't called unecessarily when xeroTenantId changes
  const getTenantInfoPromise = useMemo(() => {
    if (!showXeroConfigurationCard) return null;
    return customIntegrationXero.getXeroTenantInfo();
  }, [customIntegrationXero, showXeroConfigurationCard]);

  if (!showXeroConfigurationCard) {
    return null;
  }

  return (
    <DataFetcher
      fetch={async ({ customIntegrationXero }) => {
        await Promise.all([
          getTenantInfoPromise,
          customIntegrationXero.getSettings()
        ]);
      }}
      refetchId={context.xeroTenantId}
      fallback={<Spinner />}
    >
      {() => (
        <Card
          heading="Configuration"
          iconName="Settings"
          button={context.xeroTenants.length > 0 ? undefined : <XeroButton />}
          styles={{
            heading: { fontSize: 20, fontWeight: FontWeights.semibold },
            subHeading: { fontSize: 12 },
            subComponentStyles: {
              tile: {
                content: { padding: 24 }
              }
            }
          }}
        >
          <Stack
            horizontal
            horizontalAlign="space-between"
            tokens={{ childrenGap: 16 }}
          >
            <Stack grow verticalAlign="start" tokens={{ childrenGap: 4 }}>
              <Text variant="medium">
                {context.xeroTenants.length > 0 ? (
                  <XeroViewSelector />
                ) : (
                  "Log into your Xero account and select the organisation you would like to connect to Omni."
                )}
              </Text>
            </Stack>
          </Stack>
        </Card>
      )}
    </DataFetcher>
  );
});
